// RUTA: src/components/fitness/CardioHistorySection.tsx
//
// Historial de cardio del cliente: heatmap 12 semanas con adherencia
// vs meta diaria, drill-down al día seleccionado con desglose de
// sesiones, y stats del mes en curso.
//
// Reutiliza el patrón visual de CalorieHeatmap del coach para que
// los clientes vean su adherencia con el mismo lenguaje visual.

import { useEffect, useMemo, useState } from 'react';
import { HeartPulse, Target, Flame, X, Camera } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cachedQuery } from '@/lib/offlineStorage';
import { useReconnectRefetch } from '@/hooks/useReconnectRefetch';
import { getEquipmentLabel } from '@/lib/cardioEquipment';
import { ResilientImage } from '@/components/ResilientImage';
import { CoachStreakCard } from './CoachStreakCard';

interface CardioLogRow {
  id: number;
  description: string | null;
  calories_burned: number;
  log_date: string;
  equipment: string | null;
  duration_minutes: number | null;
  evidence_photo_url: string | null;
}

interface Props {
  /** ID del cliente cuyas sesiones se muestran */
  userId: string;
  /** Perfil activo (multi-perfil: Joel / Alexa) */
  profileName: string;
  /** Meta diaria de kcal de cardio */
  dailyGoal: number;
  /** Trigger para refrescar tras un nuevo log */
  refreshKey?: number;
}

const WEEKS = 12;
const TOTAL_DAYS = WEEKS * 7;
const DAY_LABELS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

function formatLocalDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

type Band = 'none' | 'on-target' | 'close' | 'off';

function bandFor(kcal: number, goal: number): Band {
  if (kcal === 0) return 'none';
  if (goal <= 0) return 'on-target';
  const pct = (kcal / goal) * 100;
  if (pct >= 90) return 'on-target';
  if (pct >= 60) return 'close';
  return 'off';
}

function colorForBand(band: Band): string {
  switch (band) {
    case 'on-target': return 'bg-green-500 border-green-600';
    case 'close':     return 'bg-yellow-500/70 border-yellow-500/80';
    case 'off':       return 'bg-orange-500/60 border-orange-500/70';
    default:          return 'bg-muted/40 border-border/40';
  }
}

export function CardioHistorySection({ userId, profileName, dailyGoal, refreshKey }: Props) {
  const [logs, setLogs] = useState<CardioLogRow[]>([]);
  const [pinnedDate, setPinnedDate] = useState<string | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  // Fetch últimos 12 semanas de cardio
  const fetchHistory = () => {
    if (!userId) return;
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - (TOTAL_DAYS - 1));
    const startStr = formatLocalDate(start);
    // Cache key incluye fecha de inicio + profile para no colisionar
    // entre días/perfiles. El heatmap es lo primero que ve el cliente
    // al entrar a Cardio — si offline no carga, ven heatmap vacío.
    cachedQuery<CardioLogRow[]>(
      `cardio_history_${userId}_${profileName}_${startStr}`,
      () => supabase
        .from('exercise_logs')
        .select('id, description, calories_burned, log_date, equipment, duration_minutes, evidence_photo_url')
        .eq('user_id', userId)
        .eq('profile_name', profileName)
        .eq('exercise_type', 'cardio')
        .gte('log_date', startStr)
        .order('log_date', { ascending: false })
    ).then(({ data }) => {
      setLogs((data || []) as CardioLogRow[]);
    });
  };
  useEffect(() => { fetchHistory(); }, [userId, profileName, refreshKey]);
  useReconnectRefetch(fetchHistory);

  // Agrupar por fecha
  const byDate = useMemo(() => {
    const map: Record<string, CardioLogRow[]> = {};
    for (const l of logs) (map[l.log_date] ||= []).push(l);
    return map;
  }, [logs]);

  // Construcción de columnas (semanas) — mismo patrón que CalorieHeatmap
  const grid = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days: { date: string; kcal: number; band: Band; sessions: number }[] = [];
    for (let i = TOTAL_DAYS - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const dateStr = formatLocalDate(d);
      const sessions = byDate[dateStr] || [];
      const kcal = sessions.reduce((s, x) => s + Number(x.calories_burned), 0);
      days.push({ date: dateStr, kcal, sessions: sessions.length, band: bandFor(kcal, dailyGoal) });
    }
    const columns: typeof days[] = [];
    let currentCol: typeof days = [];
    for (const day of days) {
      const dayOfWeek = (new Date(day.date + 'T12:00:00').getDay() + 6) % 7;
      if (currentCol.length === 0 && dayOfWeek !== 0 && columns.length === 0) {
        for (let p = 0; p < dayOfWeek; p++) currentCol.push({ date: '', kcal: 0, sessions: 0, band: 'none' });
      }
      currentCol.push(day);
      if (currentCol.length === 7 || dayOfWeek === 6) {
        while (currentCol.length < 7) currentCol.push({ date: '', kcal: 0, sessions: 0, band: 'none' });
        columns.push(currentCol);
        currentCol = [];
      }
    }
    if (currentCol.length > 0) {
      while (currentCol.length < 7) currentCol.push({ date: '', kcal: 0, sessions: 0, band: 'none' });
      columns.push(currentCol);
    }
    return columns;
  }, [byDate, dailyGoal]);

  // Racha de logueo: días consecutivos hacia atrás (relativos a HOY)
  // con AL MENOS 1 sesión de cardio. Misma filosofía que en nutrición:
  // refuerza el HÁBITO, no la perfección. Si hoy todavía no hay log,
  // empezamos a contar desde ayer (no rompe la racha aún).
  const streak = useMemo(() => {
    if (logs.length === 0) return 0;
    const distinctDays = new Set(logs.map(l => l.log_date));
    let count = 0;
    const todayLocal = (() => {
      const d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    })();
    const cursor = new Date(todayLocal + 'T12:00:00');
    let isFirstDay = true;
    let safety = 0;
    while (safety++ < 40) {
      const yyyy = cursor.getFullYear();
      const mm = String(cursor.getMonth() + 1).padStart(2, '0');
      const dd = String(cursor.getDate()).padStart(2, '0');
      const key = `${yyyy}-${mm}-${dd}`;
      if (distinctDays.has(key)) {
        count++;
      } else if (!isFirstDay) {
        break;
      }
      isFirstDay = false;
      cursor.setDate(cursor.getDate() - 1);
    }
    return count;
  }, [logs]);

  // ¿El cliente registró cardio HOY? Determina el tono del mensaje
  // (afirmar lo que hizo vs invitar a mover el cuerpo hoy).
  const loggedTodayCardio = useMemo(() => {
    const todayStr = (() => {
      const d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    })();
    return logs.some(l => l.log_date === todayStr);
  }, [logs]);

  // Stats del mes en curso
  const monthStats = useMemo(() => {
    const now = new Date();
    const firstOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const dayOfMonth = now.getDate();
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const monthLogs = logs.filter(l => l.log_date >= firstOfMonth);
    const totalKcal = monthLogs.reduce((s, l) => s + Number(l.calories_burned), 0);
    const expected = dailyGoal * dayOfMonth;
    const fullMonth = dailyGoal * daysInMonth;
    // Días distintos con cardio este mes
    const distinctDays = new Set(monthLogs.map(l => l.log_date)).size;
    return { totalKcal, expected, fullMonth, distinctDays, dayOfMonth, daysInMonth };
  }, [logs, dailyGoal]);

  // Carga URL firmada para preview de foto
  useEffect(() => {
    if (!photoUrl || photoUrl.startsWith('https://')) return;
    let cancelled = false;
    supabase.storage
      .from('cardio-evidence')
      .createSignedUrl(photoUrl, 60 * 10)
      .then(({ data }) => {
        if (cancelled) return;
        setPhotoUrl(data?.signedUrl || null);
      });
    return () => { cancelled = true; };
  }, [photoUrl]);

  const pinnedSessions = pinnedDate ? (byDate[pinnedDate] || []) : [];

  return (
    <section className="rounded-xl border-2 border-border bg-card p-4 md:p-5">
      <div className="flex items-center gap-2 mb-4">
        <HeartPulse className="h-5 w-5 text-accent" />
        <h3 className="text-sm font-black uppercase tracking-widest">Historial de Cardio</h3>
        <span className="text-[10px] text-muted-foreground font-bold ml-auto">{WEEKS} semanas</span>
      </div>

      {/* Racha y mensaje del coach: ahora viven ARRIBA de la sección
       *  Cardio (CardioCoachCard en Index.tsx), no aquí en Historial.
       *  Patrón consistente con Nutrición & Calorías. */}

      {/* Stats del mes */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="p-3 rounded-lg bg-secondary/40 border">
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground mb-1">
            <Flame className="h-3 w-3" /> Este mes
          </div>
          <div className="text-base font-black">
            {monthStats.totalKcal.toLocaleString()}
            <span className="text-[10px] text-muted-foreground font-bold"> kcal</span>
          </div>
        </div>
        <div className="p-3 rounded-lg bg-secondary/40 border">
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground mb-1">
            <Target className="h-3 w-3" /> Días activos
          </div>
          <div className="text-base font-black">
            {monthStats.distinctDays}
            <span className="text-[10px] text-muted-foreground font-bold"> / {monthStats.dayOfMonth}</span>
          </div>
        </div>
        <div className="p-3 rounded-lg bg-secondary/40 border">
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground mb-1">
            <HeartPulse className="h-3 w-3" /> Meta mes
          </div>
          <div className="text-base font-black">
            {monthStats.fullMonth.toLocaleString()}
            <span className="text-[10px] text-muted-foreground font-bold"> kcal</span>
          </div>
        </div>
      </div>

      {/* Heatmap */}
      <div className="flex gap-2 items-start overflow-x-auto pb-2">
        <div className="flex flex-col gap-1 pt-0.5 shrink-0">
          {DAY_LABELS.map(label => (
            <div key={label} className="h-4 text-[10px] text-muted-foreground flex items-center pr-1">
              {label}
            </div>
          ))}
        </div>
        <div className="flex gap-1 shrink-0">
          {grid.map((col, ci) => (
            <div key={ci} className="flex flex-col gap-1">
              {col.map((cell, ri) => (
                <div
                  key={ri}
                  onClick={() => {
                    if (!cell.date) return;
                    setPinnedDate(prev => (prev === cell.date ? null : cell.date));
                  }}
                  className={`w-4 h-4 rounded-sm border ${cell.date ? colorForBand(cell.band) + ` cursor-pointer hover:ring-2 hover:ring-accent transition-all ${pinnedDate === cell.date ? 'ring-2 ring-accent' : ''}` : 'opacity-0 pointer-events-none'}`}
                  title={cell.date ? `${cell.date}: ${cell.kcal} kcal` : ''}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Leyenda */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-[10px] text-muted-foreground">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-muted/40 border border-border/40" /> Sin cardio
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-green-500 border-green-600" /> En meta (≥90%)
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-yellow-500/70 border-yellow-500/80" /> Cerca (60–89%)
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-orange-500/60 border-orange-500/70" /> Lejos (&lt;60%)
        </div>
      </div>

      {/* Detalle del día seleccionado */}
      {pinnedDate && (
        <div className="mt-3 p-3 rounded-lg bg-card border-2 text-sm relative">
          <button
            onClick={() => setPinnedDate(null)}
            className="absolute top-1.5 right-1.5 w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Cerrar detalle"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="font-bold pr-8">
            {new Date(pinnedDate + 'T12:00:00').toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
          {pinnedSessions.length > 0 ? (
            <>
              <div className="text-xs text-muted-foreground mt-1">
                <span className="font-bold text-foreground">
                  {pinnedSessions.reduce((s, l) => s + Number(l.calories_burned), 0)}
                </span>
                {dailyGoal > 0 && <> / {dailyGoal} kcal · {pinnedSessions.length} sesión{pinnedSessions.length !== 1 ? 'es' : ''}</>}
              </div>
              <ul className="mt-2 space-y-1.5 pt-2 border-t border-border">
                {pinnedSessions.map(s => (
                  <li key={s.id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="flex-1 min-w-0">
                      <span className="font-bold truncate block">
                        {s.description || getEquipmentLabel(s.equipment) || 'Cardio'}
                      </span>
                      {s.duration_minutes && (
                        <span className="text-muted-foreground">{s.duration_minutes} min</span>
                      )}
                    </span>
                    <span className="font-mono font-bold shrink-0">{s.calories_burned} kcal</span>
                    {s.evidence_photo_url && (
                      <button
                        onClick={() => setPhotoUrl(s.evidence_photo_url)}
                        className="text-accent shrink-0"
                        aria-label="Ver foto"
                      >
                        <Camera className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div className="text-xs text-muted-foreground mt-1 italic">Sin cardio registrado este día</div>
          )}
        </div>
      )}

      {/* Modal foto */}
      {photoUrl && photoUrl.startsWith('https://') && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPhotoUrl(null)}
        >
          <div className="relative max-w-2xl max-h-[90vh] w-full" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setPhotoUrl(null)}
              className="absolute -top-2 -right-2 z-10 bg-card border-2 border-border rounded-full w-10 h-10 flex items-center justify-center font-black text-lg"
              aria-label="Cerrar"
            >×</button>
            <ResilientImage
              src={photoUrl}
              alt="Evidencia"
              className="w-full h-auto rounded-xl"
              fallback={
                <div className="text-white p-8 text-center bg-white/5 rounded-xl">
                  No se pudo cargar la foto
                </div>
              }
            />
          </div>
        </div>
      )}
    </section>
  );
}
